import {
  Box,
  InputBase,
  IconButton,
  Paper,
  Typography,
  Select,
  MenuItem,
  ButtonGroup,
  Button,
  Checkbox,
  ListItemText,
  Popover,
  List,
  ListItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ThemePalette } from 'mui.theme';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

const VIEW_LABEL_KEYS: Record<string, string> = {
  Active: 'active',
  Inactive: 'inactive',
  Solved: 'solved',
};

const SORT_LABEL_KEYS: Record<string, string> = {
  'Insight Start': 'insight_start',
  Severity: 'severity',
};

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  marginLeft: '10px',
  flex: 1,
  '& .MuiInputBase-input': {
    fontSize: 12,
  },
  minWidth: '300px',
}));

const SearchButton = styled(IconButton)(({ theme }) => ({
  padding: 10,
}));

const SearchContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
}));

export const TableFilter = ({
  enableDarkTheme,
  searchTerm,
  setSearchTerm,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  selectedView,
  setSelectedView,
  selectedType,
  setSelectedType,
  selectedIssues,
  setSelectedIssues,
  uniqueIssues,
}: {
  enableDarkTheme: boolean;
  searchTerm: string;
  setSearchTerm: any;
  sortBy: string;
  setSortBy: any;
  sortOrder: 'asc' | 'desc';
  setSortOrder: any;
  selectedView: string;
  setSelectedView: any;
  selectedType: string;
  setSelectedType: any;
  selectedIssues: string[];
  setSelectedIssues: any;
  uniqueIssues: string[];
}) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme ? _palette.dark : _palette.light;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    setSearchTerm(newValue);
    if (selectedIssues.length > 0) {
      setSelectedIssues([]);
    }
  };

  const inputValue =
    selectedIssues.length > 0 ? selectedIssues.join(', ') : searchTerm;

  const handleSearchClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleIssue = (issue: string) => {
    if (selectedIssues.includes(issue)) {
      setSelectedIssues(selectedIssues.filter((i: string) => i !== issue));
    } else {
      setSelectedIssues([...selectedIssues, issue]);
    }
  };

  const handleSelectAll = () => {
    if (selectedIssues.length === uniqueIssues.length) {
      setSelectedIssues([]);
    } else {
      setSelectedIssues([...uniqueIssues]);
    }
  };

  const open = Boolean(anchorEl);
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        marginLeft: '10px',
        marginRight: '10px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <SearchContainer
          elevation={0}
          sx={{
            backgroundColor: enableDarkTheme
              ? palette.toolBarBackground
              : palette.menuOptions,
            border: `1px solid ${palette.borderGrey}`,
          }}
        >
          <StyledInputBase
            placeholder={t('search_for_an_asset_insight')}
            inputProps={{ 'aria-label': t('search_for_an_asset_insight') }}
            value={inputValue}
            onChange={handleChange}
            sx={{
              color: palette.textPrimary,
              '& .MuiInputBase-input::placeholder': {
                color: palette.textSecondary,
                opacity: 1,
              },
            }}
          />
          <SearchButton
            type="button"
            aria-label="search"
            onClick={handleSearchClick}
            sx={{ color: palette.textSecondary }}
          >
            <ExpandMoreIcon />
          </SearchButton>
        </SearchContainer>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              backgroundColor: enableDarkTheme
                ? palette.menuOptions
                : palette.boxBackground,
              maxHeight: 400,
              width: 350,
              mt: 1,
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              color: palette.textPrimary,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                pb: 1,
                borderBottom: `1px solid ${
                  enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)'
                }`,
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {t('filter_by_issues')}
              </Typography>
              <Button
                size="small"
                onClick={handleSelectAll}
                sx={{
                  textTransform: 'none',
                  color: palette.primary,
                  fontSize: '12px',
                }}
              >
                {selectedIssues.length === uniqueIssues.length
                  ? t('deselect_all')
                  : t('select_all')}
              </Button>
            </Box>
            <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
              {uniqueIssues.map((issue) => (
                <ListItem
                  key={issue}
                  dense
                  onClick={() => {
                    handleToggleIssue(issue);
                  }}
                  sx={{
                    px: 0,
                    '&:hover': {
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <Checkbox
                    checked={selectedIssues.includes(issue)}
                    size="small"
                    sx={{
                      color: palette.textSecondary,
                      '&.Mui-checked': {
                        color: palette.primary,
                      },
                    }}
                  />
                  <ListItemText
                    primary={issue}
                    primaryTypographyProps={{
                      fontSize: '14px',
                      color: palette.textPrimary,
                    }}
                  />
                </ListItem>
              ))}
            </List>
            {selectedIssues.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  pt: 1,
                  borderTop: `1px solid ${
                    enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.1)'
                  }`,
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '12px' }}>
                  {t('issues_selected_count', { count: selectedIssues.length })}
                </Typography>
              </Box>
            )}
          </Box>
        </Popover>
        {/* Selected View */}
        <ButtonGroup
          size="small"
          variant="outlined"
          aria-label={t('basic_button_group')}
          sx={{
            border: `1px solid rgba(255, 255, 255, 0.15)`,
            marginLeft: '20px',
          }}
        >
          {['Active', 'Inactive', 'Solved'].map((item) => {
            return (
              <Button
                key={item}
                size="small"
                style={{
                  backgroundColor:
                    selectedView === item
                      ? palette.primary
                      : enableDarkTheme
                      ? _palette.dark.toolBarBackground
                      : _palette.light.toolBarBackground,
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : selectedView === item
                    ? ThemePalette.typography.white
                    : ThemePalette.typography.black,
                  textTransform: 'none',
                  paddingLeft: selectedView === item ? '10px' : '15px',
                  paddingRight: selectedView === item ? '10px' : '15px',
                }}
                onClick={() => {
                  setSelectedView(item);
                }}
              >
                {t(VIEW_LABEL_KEYS[item] ?? item)}
              </Button>
            );
          })}
        </ButtonGroup>
      </Box>

      <Box
        sx={{
          display: 'flex',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            marginTop: '10px',
          }}
        >
          {t('sort_by')}
        </Typography>
        <Select
          labelId="parts-recommendation-sub-components-sort"
          value={sortBy}
          onChange={(e: any) => {
            setSortBy(e.target.value);
          }}
          sx={{
            height: '40px',
            fontSize: '14px',
            marginLeft: '10px',
            marginRight: '20px',
            backgroundColor: enableDarkTheme
              ? palette.toolBarBackground
              : palette.menuOptions,
            color: palette.textPrimary,
            '& .MuiSelect-icon': {
              color: palette.textSecondary,
            },
            '& fieldset': {
              borderColor: palette.textPrimary,
            },
            ...(enableDarkTheme && {
              '&.MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: palette.borderGrey,
                },
              },
            }),
          }}
          MenuProps={{
            PaperProps: {
              style: {
                backgroundColor: enableDarkTheme
                  ? palette.menuOptions
                  : palette.boxBackground,
                color: palette.textPrimary,
                fontSize: '14px',
              },
            },
          }}
        >
          {['Insight Start', 'Severity'].map((type, index) => (
            <MenuItem
              key={index}
              value={type}
              sx={{
                fontSize: '14px',
              }}
            >
              {t(SORT_LABEL_KEYS[type] ?? type)}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );
};
